import { NativeFetchClient, type HttpClient, type JsonApiRequest, type JsonApiResponse } from '@jaspr/client-js'
import type { KeycloakLike } from '@/plugins/keycloak'
import { ApiError, apiErrorFromResponse } from './errors'

const MIN_VALIDITY_SECONDS = 30

/** A token valid for 30 s more; a failed refresh means the SSO session ended, so the user logs in again. */
export async function freshToken(keycloak: KeycloakLike): Promise<string> {
    try {
        await keycloak.updateToken(MIN_VALIDITY_SECONDS)
    } catch {
        await keycloak.login()
        throw new ApiError(401, 'session-expired', 'Session expired', '')
    }
    if (!keycloak.token) {
        await keycloak.login()
        throw new ApiError(401, 'session-expired', 'Session expired', '')
    }
    return keycloak.token
}

/** client-js's HTTP client with a fresh bearer token on every request (spec §4.4). */
export class KeycloakHttpClient implements HttpClient {
    readonly #keycloak: KeycloakLike
    readonly #inner: HttpClient

    constructor(keycloak: KeycloakLike, inner: HttpClient = new NativeFetchClient()) {
        this.#keycloak = keycloak
        this.#inner = inner
    }

    async request(request: JsonApiRequest): Promise<JsonApiResponse> {
        const headers = new Headers(request.headers)
        headers.set('Authorization', `Bearer ${await freshToken(this.#keycloak)}`)
        return this.#inner.request({ ...request, headers })
    }
}

/** fetch for the routes outside JSON:API (/api/me, /api/reports/*, /api/rpc); a failed status throws an ApiError. */
export async function authorizedFetch(
    keycloak: KeycloakLike,
    url: string,
    init: RequestInit = {},
    fetchImpl: typeof fetch = (input, options) => globalThis.fetch(input, options),
): Promise<Response> {
    const headers = new Headers(init.headers)
    headers.set('Authorization', `Bearer ${await freshToken(keycloak)}`)
    const response = await fetchImpl(url, { ...init, headers })
    if (!response.ok) {
        throw await apiErrorFromResponse(response)
    }
    return response
}
